import { Box } from "@mui/material";
import { deepPurple, grey } from "@mui/material/colors";

import OverflowChipRow from "../components/list/overflow-chip-row";
import MalnutritionStatusChip, {
  statusLabel,
} from "../components/malnutrition/malnutrition-status-chip";
import QuickFilter from "../utils/filter/quick-filter";
import { getPatientIdentifier } from "../utils/patient-identifier";

import type { GetApplyQuickFilterFn, GridColDef } from "@mui/x-data-grid";
import type Configuration from "../types/Configuration";
import type { MalnutritionStatus } from "../types/Malnutrition";
import type MalnutritionListEntry from "../types/MalnutritionListEntry";

// Worst first: the list opens on this order.
const RISK_RANK: Record<MalnutritionStatus, number> = {
  high_risk: 0,
  medium_risk: 1,
  protocol: 2,
  consult: 3,
  following: 4,
};

const riskRank = (status: MalnutritionStatus | null): number =>
  status ? RISK_RANK[status] : Object.keys(RISK_RANK).length;

// The search sees the label only, not the raw status key underneath.
const matchStatusLabel: GetApplyQuickFilterFn = (value, colDef, apiRef) => {
  const match = QuickFilter(value, colDef, apiRef);
  return (
    match &&
    ((cell, row, column, api) => match(cell?.risk_level, row, column, api))
  );
};

const sxStyles = {
  indicatorChip: {
    display: "inline-flex",
    alignItems: "center",
    height: "1.875rem",
    boxSizing: "border-box",
    padding: "0 0.6875rem",
    borderRadius: "0.3125rem",
    border: `1px solid ${deepPurple[100]}`,
    background: deepPurple[50],
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: "0.875rem",
    whiteSpace: "nowrap",
  },
  // Same height as a chip, so rows keep one height with or without indicators.
  noIndicators: {
    display: "flex",
    alignItems: "center",
    minHeight: "1.875rem",
    color: grey[600],
  },
};

export const getMalnutritionListColumns = (
  configuration?: Configuration | null
): GridColDef<MalnutritionListEntry>[] => [
  {
    field: "current_bed",
    headerName: "Bed",
    flex: 0.6,
    minWidth: 90,
  },
  {
    field: "current_department",
    headerName: "Department",
    flex: 1,
    minWidth: 130,
  },
  {
    field: "mrn",
    headerName: "Patient ID",
    flex: 1,
    minWidth: 130,
    valueGetter: (value, row) => getPatientIdentifier(row, configuration),
  },
  {
    field: "full_name",
    headerName: "Patient Name",
    flex: 1.4,
    minWidth: 170,
    valueGetter: (value, row) =>
      row.age != null ? `${row.full_name} (${row.age})` : row.full_name,
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    minWidth: 140,
    // `risk_level` is what the search matches on.
    valueGetter: (value, row) => ({
      risk_level: statusLabel(row.status),
      status: row.status,
    }),
    getApplyQuickFilterFn: matchStatusLabel,
    sortComparator: (a, b) => riskRank(a.status) - riskRank(b.status),
    renderCell: (params) => (
      <MalnutritionStatusChip status={params.row.status} />
    ),
  },
  {
    field: "criteria_met",
    headerName: "Clinical Indicators of Malnutrition",
    flex: 3.3,
    minWidth: 260,
    sortable: false,
    getApplyQuickFilterFn: QuickFilter,
    // "|"-joined: the search matches any title, and the Clinical indicators
    // filter splits on "|".
    valueGetter: (value, row) =>
      row.criteria_met.map((criterion) => criterion.title).join("|"),
    renderCell: (params) =>
      params.row.criteria_met.length > 0 ? (
        <OverflowChipRow
          labels={params.row.criteria_met.map((criterion) => criterion.title)}
          chipSx={sxStyles.indicatorChip}
          tabIndex={params.tabIndex}
        />
      ) : (
        <Box sx={sxStyles.noIndicators}>—</Box>
      ),
  },
];
